import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { RollingViewerItem, RollingViewerItemRef } from 'app-components/rolling/RollingViewerItem';
import { debounce, throttle } from 'utils/common';
import { showToast } from 'utils/modal';
import { useRollingDispatchState, useRollingState } from 'contexts/RollingProvider';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { useGetRelayRollingQuery } from 'queries/relay/useGetRelayRollingQuery';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetRollingReactionQuery } from 'queries/rollingReaction/useGetRollingReactionQuery';
import { useAuthState } from 'contexts/AuthProvider';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.RollingViewer,
};

const BottomIndicatorHeight = 200;

export const RollingViewer = ({ rollingId }: { rollingId: number }) => {
  const { includeAdult } = useAuthState();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const childrenRef = useRef<{ [key: number]: RollingViewerItemRef }>({});
  const [isLoaded, setLoaded] = useState(false);
  const { data: rollingData } = useGetRelayRollingQuery(rollingId, includeAdult);
  const { data: reactionData } = useGetRollingReactionQuery(rollingId);

  const { hasNextPage, fetchNextPage, rollings, rollingReactions, profiles, contents } = useRollingState();
  const { dispatchAction } = useRollingDispatchState();

  const { tracker } = useTrackingState();
  useEffect(() => {
    tracker.track(TrackingEventType.ViewRollingList, { trackerExtraInfo });
  }, [tracker]);

  const handleChildrenRef = useCallback(
    (ref: RollingViewerItemRef | null) => {
      if (ref) {
        if (!isLoaded) {
          setLoaded(true);
        }
        childrenRef.current[ref.rollingId] = ref;
      }
    },
    [isLoaded],
  );

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = debounce(() => Object.values(childrenRef.current).map(child => child.onScroll()), 100);

    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  // 마지막 롤링 체크
  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = throttle(() => {
      if (!container || container.scrollHeight <= BottomIndicatorHeight) {
        return;
      }

      if (
        Object.keys(childrenRef.current).length > 0 &&
        container.scrollHeight - BottomIndicatorHeight < container.scrollTop + container.clientHeight
      ) {
        showToast('마지막 롤링이에요.');
      }
    }, 200);

    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  const rollingContent =
    (rollingData?.rolling?.contentId && rollingData.contents[rollingData.rolling.contentId]) || undefined;
  const rollingContentAuthorProfile =
    (rollingContent && rollingContent.profileId && rollingData?.profiles[rollingContent.profileId]) || undefined;

  return (
    <StyledContainer ref={containerRef} hasNext={hasNextPage} loadMore={fetchNextPage}>
      {rollingData?.rolling && (
        <RollingViewerItem
          trackerExtraInfo={trackerExtraInfo}
          ref={handleChildrenRef}
          key={rollingData.rolling.id}
          showMoreMenu
          onDispatchAction={dispatchAction}
          rolling={rollingData.rolling}
          rollingReaction={reactionData?.rollingReaction}
          profile={rollingData?.profiles[rollingData.rolling.profileId]}
          content={rollingContent}
          contentAuthorProfile={rollingContentAuthorProfile}
        />
      )}
      {rollings.map(rolling => {
        const content = (rolling.contentId && contents[rolling.contentId]) || undefined;
        const contentAuthorProfile = (content && content.profileId && profiles[content.profileId]) || undefined;

        return (
          <RollingViewerItem
            trackerExtraInfo={trackerExtraInfo}
            ref={handleChildrenRef}
            key={rolling.id}
            showMoreMenu
            onDispatchAction={dispatchAction}
            rolling={rolling}
            rollingReaction={rollingReactions[rolling.id]}
            profile={profiles[rolling.profileId]}
            content={content}
            contentAuthorProfile={contentAuthorProfile}
          />
        );
      })}
      <StyledBottomIndicator />
    </StyledContainer>
  );
};

const StyledContainer = styled(InfiniteScrollWrapper)`
  position: relative;

  width: 100%;
  height: 100%;

  scroll-snap-type: y mandatory;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledBottomIndicator = styled.div`
  height: ${BottomIndicatorHeight}px;
`;
